import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Save, Palette } from 'lucide-react';
import { toast } from 'sonner';
import { useSettings } from '../../contexts/SettingsContext';
import { COLOR_OPTIONS, getIcon } from '../../lib/icons';
import IconPicker from './IconPicker';

const MenuEditor = ({ items, onChange, prefix }) => {
  const updateItem = (index, patch) => {
    const next = items.map((it, i) => (i === index ? { ...it, ...patch } : it));
    onChange(next);
  };

  return (
    <div className="space-y-4">
      {items.map((item, index) => {
        const Preview = getIcon(item.icon);
        return (
          <div
            key={item.id}
            data-testid={`${prefix}-row-${item.id}`}
            className="flex flex-col md:flex-row md:items-center gap-4 p-4 rounded-2xl border border-stone-200 bg-stone-50/50"
          >
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
              style={{ backgroundColor: `${item.color}1A` }}
            >
              <Preview className="w-6 h-6" style={{ color: item.color }} />
            </div>

            <input
              type="text"
              data-testid={`${prefix}-label-${item.id}`}
              value={item.label}
              onChange={(e) => updateItem(index, { label: e.target.value })}
              className="flex-1 px-3 py-2 rounded-xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none font-body text-sm"
              placeholder="Label menu"
            />

            <IconPicker
              value={item.icon}
              onChange={(icon) => updateItem(index, { icon })}
              testId={`${prefix}-iconpicker-${item.id}`}
            />

            <div className="flex items-center gap-1.5 flex-wrap">
              {COLOR_OPTIONS.map((color) => (
                <button
                  key={color}
                  type="button"
                  data-testid={`${prefix}-color-${item.id}-${color}`}
                  onClick={() => updateItem(index, { color })}
                  className={`w-6 h-6 rounded-full transition-transform ${
                    item.color === color ? 'ring-2 ring-offset-2 ring-stone-400 scale-110' : ''
                  }`}
                  style={{ backgroundColor: color }}
                  aria-label={`Warna ${color}`}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const IconCustomizer = () => {
  const { settings, updateSettings } = useSettings();
  const [homeMenu, setHomeMenu] = useState(settings.home_menu || []);
  const [navMenu, setNavMenu] = useState(settings.nav_menu || []);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettings({ home_menu: homeMenu, nav_menu: navMenu });
      toast.success('Ikon berhasil diperbarui!');
    } catch (error) {
      toast.error('Gagal menyimpan ikon');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <div className="bg-white rounded-2xl border border-stone-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-full bg-amber-50 flex items-center justify-center">
            <Palette className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <h3 className="text-lg font-heading font-bold text-primary-dark">Ikon Spiritual Kit (Halaman Depan)</h3>
            <p className="text-sm font-body text-text-secondary">Ubah label, ikon, dan warna menu di Home</p>
          </div>
        </div>
        <MenuEditor items={homeMenu} onChange={setHomeMenu} prefix="home-menu" />
      </div>

      <div className="bg-white rounded-2xl border border-stone-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-full bg-emerald-50 flex items-center justify-center">
            <Palette className="w-6 h-6 text-emerald-700" />
          </div>
          <div>
            <h3 className="text-lg font-heading font-bold text-primary-dark">Ikon Bottom Navigation</h3>
            <p className="text-sm font-body text-text-secondary">Ubah label, ikon, dan warna menu navigasi bawah</p>
          </div>
        </div>
        <MenuEditor items={navMenu} onChange={setNavMenu} prefix="nav-menu" />
      </div>

      <button
        data-testid="icons-save-btn"
        onClick={handleSave}
        disabled={saving}
        className="flex items-center gap-2 px-6 py-3 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full font-body font-medium transition-colors disabled:opacity-50"
      >
        <Save className="w-5 h-5" />
        {saving ? 'Menyimpan...' : 'Simpan Ikon'}
      </button>
    </motion.div>
  );
};

export default IconCustomizer;
